/**
 * Actycrea: video.esm.js (ES6)
 * MIT
 *
 * Controls a hero <video> element — mute/unmute and play/pause — and keeps
 * every control button that targets it in sync (native `volumechange`,
 * `play` and `pause` events). The header sound button and a future
 * `module-video` play/pause button can both point at the same <video> and
 * share one instance.
 *
 * The wave animation of the sound-toggle button is purely decorative and
 * does not depend on a bound <video> existing yet — it is started for every
 * `[data-{PREFIX}-sound-toggle]` button found in the page, independently of
 * whether `module-video` (and its <video>) has been built.
 */
import { gsap } from 'gsap';
import { PREFIX } from '../helpers/config.js';

const ATTR_SOUND  = `data-${PREFIX}-sound-toggle`;
const ATTR_PLAY   = `data-${PREFIX}-play-toggle`;
const ATTR_TARGET = `data-${PREFIX}-video-target`;
const ATTR_LABEL_ON  = `${PREFIX}LabelOn`;
const ATTR_LABEL_OFF = `${PREFIX}LabelOff`;

const SELECTOR_SOUND_DATA = `[${ATTR_SOUND}]`;
const SELECTOR_PLAY_DATA  = `[${ATTR_PLAY}]`;

const XLINK_NS       = 'http://www.w3.org/1999/xlink';
const ICON_ID_PLAY   = '#custom-play';
const ICON_ID_PAUSE  = '#custom-pause';

const WAVE_PERIOD   = 44;  // px — must match the repeat unit of the wave path in partials/_sound.php
const WAVE_DURATION = 1.6; // seconds per loop

/**
 * Tracks already animated by `startWave()`, so it's never started twice on
 * the same button.
 *
 * @type {WeakSet<SVGElement>}
 */
const animatedTracks = new WeakSet( );

/**
 * Starts the flowing-wave loop of a sound-toggle button, once, and leaves it
 * running forever — the wave keeps scrolling sideways like a continuous
 * ocean wave no matter the mute state. Only its color (gray/white) changes,
 * via CSS on `[aria-pressed]`.
 *
 * @param {HTMLButtonElement} button - The sound-toggle button.
 * @returns {void}
 */
function startWave( button ){
  const track = button.querySelector( '.sound-toggle-wave-track' );
  if( !track || animatedTracks.has( track ) ) return;

  animatedTracks.add( track );
  gsap.to( track , {
    x: -WAVE_PERIOD ,
    duration: WAVE_DURATION ,
    ease: 'none' ,
    repeat: -1
  } );
}

export class Video {
  /**
   * @param {HTMLVideoElement} element
   */
  constructor( element ){
    if( !(element instanceof HTMLVideoElement) ) throw new Error('Video: element inválido');

    this._element = element;
    this._soundButtons = [ ];
    this._playButtons  = [ ];

    // bind
    this._onVolumeChange = this._onVolumeChange.bind( this );
    this._onPlay  = this._onPlay.bind( this );
    this._onPause = this._onPause.bind( this );

    // inicio
    this._element.addEventListener( 'volumechange' , this._onVolumeChange );
    this._element.addEventListener( 'play'  , this._onPlay );
    this._element.addEventListener( 'pause' , this._onPause );
  }

  /**
   * Registers a mute/unmute button for this video and syncs its initial state.
   *
   * @param {HTMLButtonElement} button
   * @returns {void}
   */
  addSoundButton( button ){
    this._soundButtons.push( button );
    button.addEventListener( 'click' , ( ) => this.toggleMute( ) );
    this._syncSoundButton( button );
  }

  /**
   * Registers a play/pause button for this video and syncs its initial state.
   *
   * @param {HTMLButtonElement} button
   * @returns {void}
   */
  addPlayButton( button ){
    this._playButtons.push( button );
    button.addEventListener( 'click' , ( ) => this.togglePlay( ) );
    this._syncPlayButton( button );
  }

  /**
   * Toggles the muted state of the video.
   *
   * @returns {void}
   */
  toggleMute( ){
    this._element.muted = !this._element.muted;
    if( !this._element.muted && this._element.paused ){
      this._element.play( ).catch( ( ) => { } );
    }
  }

  /**
   * Toggles play/pause on the video.
   *
   * @returns {void}
   */
  togglePlay( ){
    if( this._element.paused ){
      this._element.play( ).catch( ( ) => { } );
    } else {
      this._element.pause( );
    }
  }

  _onVolumeChange( ){
    this._soundButtons.forEach( ( button ) => this._syncSoundButton( button ) );
  }

  _onPlay( ){
    this._playButtons.forEach( ( button ) => this._syncPlayButton( button ) );
  }

  _onPause( ){
    this._playButtons.forEach( ( button ) => this._syncPlayButton( button ) );
  }

  _syncSoundButton( button ){
    const isOn = !this._element.muted;

    button.setAttribute( 'aria-pressed' , String( isOn ) );
    button.setAttribute( 'aria-label' , isOn ? button.dataset[ ATTR_LABEL_OFF ] : button.dataset[ ATTR_LABEL_ON ] );
  }

  _syncPlayButton( button ){
    const isPlaying = !this._element.paused;
    const label = isPlaying ? button.dataset[ ATTR_LABEL_OFF ] : button.dataset[ ATTR_LABEL_ON ];

    button.setAttribute( 'aria-pressed' , String( isPlaying ) );
    button.setAttribute( 'aria-label' , label );

    const textEl = button.querySelector( '.play-toggle-text' );
    if( textEl ) textEl.textContent = label;

    // Icon reflects the current state: play glyph while playing, pause
    // glyph once the video has been paused.
    const useEl = button.querySelector( '.play-toggle-icon use' );
    if( useEl ){
      const iconId = isPlaying ? ICON_ID_PLAY : ICON_ID_PAUSE;
      useEl.setAttribute( 'href' , iconId );
      useEl.setAttributeNS( XLINK_NS , 'xlink:href' , iconId );
    }
  }

  /**
   * Releases every listener bound by this instance.
   *
   * @returns {void}
   */
  destroy( ){
    this._element.removeEventListener( 'volumechange' , this._onVolumeChange );
    this._element.removeEventListener( 'play'  , this._onPlay );
    this._element.removeEventListener( 'pause' , this._onPause );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const instances = new Map( );

    const resolveVideo = ( button ) => {
      const targetSelector = button.getAttribute( ATTR_TARGET );
      const video = targetSelector ? root.querySelector( targetSelector ) : null;
      if( !(video instanceof HTMLVideoElement) ) return null;

      if( !instances.has( video ) ){
        instances.set( video , new Video( video ) );
      }
      return instances.get( video );
    };

    root.querySelectorAll( SELECTOR_SOUND_DATA ).forEach( ( button ) => {
      // Decorative wave: always runs, even before `module-video` exists.
      startWave( button );

      const instance = resolveVideo( button );
      if( instance ) instance.addSoundButton( button );
    } );

    root.querySelectorAll( SELECTOR_PLAY_DATA ).forEach( ( button ) => {
      const instance = resolveVideo( button );
      if( instance ) instance.addPlayButton( button );
    } );

    return [ ...instances.values( ) ];
  }
}

export default Video;
